// Partner answers pane, truthful edition (night R31). The pane used to leak
// raw catalog tokens ("clone", "manual_check") and, for answers written under
// an older questionnaire version, raw English key remnants ("business intro").
// Rules here: a token the client picked resolves to the SAME label the client
// saw in the runner (questionnaire.options), booleans render with their field
// label, technical fields stay out, unresolved values stay VERBATIM (never
// mangled - identifiers are identifiers), and an unknown question key gets an
// honest Czech label naming its provenance instead of an English slug.

type T = (key: string) => string;

function safe(t: T, key: string): string | null {
  try {
    return t(key);
  } catch {
    return null;
  }
}

// Resolution order mirrors what the client actually saw: runner option labels
// first, then the staff-side legacy maps.
export function answerTokenLabel(token: string, t: T): string | null {
  return (
    safe(t, `questionnaire.options.${token}`) ??
    safe(t, `staffConsole.answerChoice.${token}`) ??
    safe(t, `staffConsole.answerRange.${token}`)
  );
}

// Internal QC fields the partner never needs (the build brief pane carries
// the human facts); showing them reads as raw data.
const TECHNICAL_FIELDS = new Set(["availability", "username_availability"]);

// Identifier fields render verbatim even when their value happens to collide
// with an option token (a username named "clone" must stay "clone").
const VERBATIM_FIELDS = new Set(["username", "name", "buddy_name", "telegram_username"]);

// Booleans with a runner field label render as "label Ano/Ne" - before this,
// the client's meditation and Stripe answers were simply invisible.
const BOOLEAN_FIELDS = ["meditation", "has_stripe"] as const;

export function answerBody(row: Record<string, unknown>, value: Record<string, unknown>, t: T): string {
  const transcript = typeof row.transcript === "string" && row.transcript.trim() ? row.transcript.trim() : null;
  if (transcript) return transcript;
  if (typeof value.text === "string" && value.text.trim()) return value.text.trim();
  // Confirm-type answers (the client ticked "sedí to" or wrote a correction).
  if (typeof value.correction === "string" && value.correction.trim()) return value.correction.trim();
  if (value.confirmed === true || value.reviewed === true) return t("staffConsole.answerConfirmed");

  if (Array.isArray(value.entries)) {
    const entries = value.entries.flatMap((entry, index) => {
      if (!entry || typeof entry !== "object") return [];
      const repeat = entry as Record<string, unknown>;
      if (typeof repeat.text !== "string" || !repeat.text.trim()) return [];
      const kind = typeof repeat.real_vs_estimate === "string"
        ? answerTokenLabel(repeat.real_vs_estimate, t)
        : null;
      return [`${index + 1}. ${repeat.text.trim()}${kind ? ` (${kind})` : ""}`];
    });
    if (entries.length > 0) return entries.join("\n");
  }

  const parts: string[] = [];
  for (const field of ["mode", "model", "voice", "selected", "payment_method"]) {
    const token = value[field];
    if (typeof token === "string" && token) parts.push(answerTokenLabel(token, t) ?? token);
  }
  for (const field of ["email_range", "audience_range"]) {
    const token = value[field];
    if (typeof token === "string" && token) parts.push(answerTokenLabel(token, t) ?? token);
  }
  for (const field of BOOLEAN_FIELDS) {
    const flag = value[field];
    if (typeof flag === "boolean") {
      const label = safe(t, `questionnaire.fields.${field}`);
      if (label) parts.push(`${label} ${t(flag ? "questionnaire.yes" : "questionnaire.no")}`);
    }
  }
  if (Array.isArray(value.channels)) {
    parts.push(...value.channels.flatMap((channel) => (
      typeof channel === "string" && channel
        ? [answerTokenLabel(channel, t) ?? channel]
        : []
    )));
  }
  for (const [key, item] of Object.entries(value)) {
    if (key === "attachments" || key === "links" || item === null || item === undefined || item === "") continue;
    // `<field>_recording` holds an archived voice take's attachment id
    // (fix-q2-details F65); the transcript already sits in the field itself.
    if (key.endsWith("_recording")) continue;
    if (typeof item === "object" || typeof item === "boolean") continue;
    if (["mode", "model", "voice", "selected", "payment_method", "email_range", "audience_range"].includes(key)) continue;
    if (TECHNICAL_FIELDS.has(key)) continue;
    const text = String(item);
    // Enum-looking tokens get the client-facing label; anything unresolved
    // stays verbatim (usernames and names are not to be "humanized").
    const resolvable = !VERBATIM_FIELDS.has(key) && /^[a-z0-9_]+$/.test(text);
    parts.push((resolvable ? answerTokenLabel(text, t) : null) ?? text);
  }
  return parts.join(" · ") || t("staffConsole.answerEmpty");
}

export interface AnswerQuestion {
  label: string;
  legacySlug: string | null;
}

// Unknown key = an answer recorded under an older questionnaire version. Name
// that truth in Czech; the slug stays as a quiet technical hint so several
// legacy cards remain distinguishable.
export function answerQuestion(key: string, t: T): AnswerQuestion {
  const label = safe(t, `questionnaire.items.${key}`) ?? safe(t, `questionnaire.v2.items.${key}`);
  if (label) return { label, legacySlug: null };
  return {
    label: t("staffConsole.answerLegacy"),
    legacySlug: key.replace(/^q\d+_\d+_/, "").replaceAll("_", " ")
  };
}
